import { useState, useEffect, useMemo } from 'react';
import { FileText, Search, Download, Calendar, User, Activity, Loader2, Terminal, Building2, ChevronDown } from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';

const LOG_RANGE_DAYS = { '7d': 7, '30d': 30 };
// Upper bound for both date pickers — there's no log entry from the future.
const todayIso = new Date().toISOString().slice(0, 10);

const SEVERITY_CONFIG = { info: 'neutral', warning: 'warning', danger: 'danger' };
const ACTION_CONFIG = {
  login: { label: 'Login', icon: User },
  login_success: { label: 'Login', icon: User },
  login_failed: { label: 'Login Failed', icon: User },
  login_denied: { label: 'Access Denied', icon: User },
  account_locked: { label: 'Account Locked', icon: User },
  login_2fa_required: { label: '2FA Required', icon: User },
  login_2fa_invalid: { label: '2FA Failed', icon: User },
  login_device_unknown: { label: 'Unknown Device', icon: User },
  login_device_revoked: { label: 'Device Revoked', icon: User },
  login_cafe_denied: { label: 'Café Access Denied', icon: User },
  login_device_required: { label: 'Device ID Missing', icon: User },
  logout: { label: 'Logout', icon: User },
  update: { label: 'Update', icon: Activity },
  command: { label: 'Command', icon: Activity },
  pc_command: { label: 'Remote Command', icon: Activity },
  view: { label: 'View', icon: FileText },
  create: { label: 'Create', icon: Activity },
  user_create: { label: 'User Created', icon: User },
  password_change: { label: 'Password Change', icon: User },
  alert: { label: 'Alert', icon: Activity },
  suspend: { label: 'Suspend', icon: Activity },
  export: { label: 'Export', icon: Download },
  user_export: { label: 'User Export', icon: Download },
};

// Explicit severity for the login/auth-failure action types introduced
// alongside the richer audit detail — the old substring heuristic below
// (fallback for anything not listed here) only caught "failed"/"denied",
// missing e.g. account_locked or login_2fa_invalid entirely.
const ACTION_SEVERITY = {
  account_locked: 'danger',
  login_failed: 'danger',
  login_denied: 'danger',
  login_2fa_required: 'warning',
  login_2fa_invalid: 'danger',
  login_device_unknown: 'warning',
  login_device_revoked: 'danger',
  login_cafe_denied: 'danger',
  login_device_required: 'warning',
};

// Role → coarse display bucket, for the role filter tabs.
const ROLE_BUCKET = (role) => {
  if (role === 'superadmin') return 'superadmin';
  if (role === 'admin') return 'admin';
  return 'other';
};
const ROLE_BUCKET_LABEL = { all: 'All roles', superadmin: 'Super Admin', admin: 'Portal Admin', other: 'Other' };

const formatTs = (nsStr) => {
  const ms = Number(nsStr) / 1e6;
  if (!Number.isFinite(ms)) return nsStr;
  return new Date(ms).toLocaleString();
};

// AuditLog.timestamp was previously observed reading back as true-UTC+5:30
// on some deployments (a DB-session-timezone artifact), which would have
// required undoing that shift before correlating with Loki's real-UTC
// timestamps. Verified directly against Loki on 2026-07-08 (preprod): a
// fresh audit row's timestamp matched its own backend log line exactly, no
// offset — so this now passes the timestamp through as-is. If a future
// deployment reintroduces the drift, re-diagnose from real data rather than
// reapplying a blind correction.
const auditTsToUtcIso = (auditTimestamp) => {
  const trimmed = auditTimestamp.replace(/(\.\d{3})\d*$/, '$1'); // JS Date only parses ms precision
  return new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`).toISOString();
};

// Shared by the global fetch and the per-café accordion fetch below, so both
// sources of log rows end up in the same shape the rest of the page expects.
const transformLogRow = (log) => ({
  id: log.id,
  timestamp: log.timestamp || log.created_at,
  user: log.user_name || log.user_email || log.user_id || 'System',
  role: log.user_role || null,
  roleBucket: ROLE_BUCKET(log.user_role),
  action: log.action,
  details: log.detail || '',
  severity: ACTION_SEVERITY[log.action] || (
    log.action?.includes('failed') || log.action?.includes('denied') ? 'danger' :
    log.action?.includes('suspend') || log.action?.includes('command') ? 'warning' : 'info'
  ),
  ip: log.ip,
  cafeId: log.cafe_id ?? null,
  cafeName: log.cafe_name || null,
});

export default function AuditLogs() {
  const { hasPermission, isSuperAdmin, canSeeAsSuperAdmin } = useAuthStore();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [cafes, setCafes] = useState([]);
  const [cafesLoading, setCafesLoading] = useState(false);
  const [cafesError, setCafesError] = useState(null);
  const [cafeSearch, setCafeSearch] = useState('');
  // Which cafés are expanded in the accordion panel below — multi-select, so
  // expanding café 1 and then café 2 leaves both open, each showing its own
  // log list (this is the whole point vs. the old single-select dropdown).
  const [expandedCafes, setExpandedCafes] = useState({});
  // cafe_id -> { loading, error, logs } — fetched the first time that café's
  // row is expanded, then cached so collapsing/re-expanding is instant.
  const [cafeLogsById, setCafeLogsById] = useState({});
  const canExport = hasPermission(PERMISSIONS.EXPORT_REPORTS) || isSuperAdmin();
  const showCafePanel = canSeeAsSuperAdmin();

  // Date range for the global log list AND its export — GET /audit/ already
  // accepts start/end (see backend app.api.endpoints.audit.list_logs), it
  // just had no UI here yet. "All time" (default) matches the original
  // unfiltered fetch. Not applied to the per-café accordion below — each
  // row's fetch is cached by cafe_id alone, and re-scoping it per range
  // would need cache invalidation wiring that isn't worth it for a rarely
  // reopened accordion row.
  const [logRange, setLogRange] = useState('all');
  const [customLogStart, setCustomLogStart] = useState('');
  const [customLogEnd, setCustomLogEnd] = useState('');

  const loadCafes = () => {
    setCafesLoading(true);
    setCafesError(null);
    api.get('/internal/cafes')
      .then(res => setCafes(res.data || []))
      .catch(err => {
        console.error('Failed to fetch cafes:', err);
        setCafesError(err.response?.data?.detail || 'Could not load cafés');
      })
      .finally(() => setCafesLoading(false));
  };

  // Fetch the cafe list once, for the super-admin café accordion panel.
  useEffect(() => {
    if (!showCafePanel) return;
    loadCafes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCafePanel]);

  const visibleCafes = useMemo(() => {
    if (!cafeSearch.trim()) return cafes;
    const q = cafeSearch.trim().toLowerCase();
    return cafes.filter(c => c.name?.toLowerCase().includes(q));
  }, [cafes, cafeSearch]);

  const anyCafeExpanded = Object.values(expandedCafes).some(Boolean);

  // start/end for the current logRange selection, or null while a custom
  // range is picked but not yet both dates. {} means "all time" (no filter).
  const logRangeParams = () => {
    if (logRange === 'all') return {};
    if (logRange === 'custom') {
      if (!customLogStart || !customLogEnd) return null;
      // /audit/ compares end with <= (inclusive), unlike the revenue
      // endpoints' exclusive-end window — so the end DATE's own day is
      // covered by pushing the time to the last instant of that day,
      // not by rolling over to the next day like those do.
      return { start: `${customLogStart}T00:00:00`, end: `${customLogEnd}T23:59:59` };
    }
    const days = LOG_RANGE_DAYS[logRange];
    const end = new Date();
    const start = new Date(end.getTime() - days * 86400000);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  // Fetch the cross-cafe log list shown when no café row below is expanded —
  // the page's original default view, now scoped by logRange (default "all
  // time" reproduces the original unfiltered fetch exactly).
  const fetchLogs = async () => {
    const params = logRangeParams();
    if (params === null) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/audit/', { params });
      setLogs((response.data || []).map(transformLogRow));
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  // Custom range fetches only on explicit "Apply" (below), not per keystroke
  // — see the identical comment on Analytics.jsx/Billing.jsx's pickers for
  // why (re-editing an already-complete date can report "complete" on the
  // very first overwritten digit, so a per-keystroke fetch there would also
  // fight the input for focus).
  useEffect(() => {
    if (logRange !== 'custom') fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logRange]);

  const applyCustomLogRange = () => {
    if (customLogStart && customLogEnd) fetchLogs();
  };

  // Lazily fetch one café's logs the first time its accordion row is
  // expanded; cached in cafeLogsById so re-expanding doesn't refetch.
  const fetchCafeLogs = async (cafeId) => {
    setCafeLogsById(prev => ({ ...prev, [cafeId]: { loading: true, error: null, logs: prev[cafeId]?.logs || [] } }));
    try {
      const response = await api.get('/audit/', { params: { cafe_id: cafeId } });
      setCafeLogsById(prev => ({ ...prev, [cafeId]: { loading: false, error: null, logs: (response.data || []).map(transformLogRow) } }));
    } catch (err) {
      console.error('Failed to fetch logs for cafe %s:', cafeId, err);
      setCafeLogsById(prev => ({
        ...prev,
        [cafeId]: { loading: false, error: err.response?.data?.detail || 'Failed to load logs', logs: [] },
      }));
    }
  };

  const toggleCafeExpand = (cafeId) => {
    const willOpen = !expandedCafes[cafeId];
    setExpandedCafes(prev => ({ ...prev, [cafeId]: willOpen }));
    if (willOpen && !cafeLogsById[cafeId]) fetchCafeLogs(cafeId);
  };

  // Per-entry "show backend logs around this time" — correlates a failure
  // (warning/danger) audit entry with the raw Loki lines emitted in a small
  // window around its timestamp, expanded inline.
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [correlated, setCorrelated] = useState({}); // id -> { loading, error, lines }

  const toggleCorrelatedLogs = async (log) => {
    if (expandedLogId === log.id) {
      setExpandedLogId(null);
      return;
    }
    setExpandedLogId(log.id);
    if (correlated[log.id]) return; // already fetched
    setCorrelated(prev => ({ ...prev, [log.id]: { loading: true, error: null, lines: [] } }));
    try {
      const response = await api.get('/internal/system-logs', {
        params: { source: 'backend', around_ts: auditTsToUtcIso(log.timestamp), window_seconds: 15, limit: 100 },
      });
      setCorrelated(prev => ({ ...prev, [log.id]: { loading: false, error: null, lines: response.data?.lines || [] } }));
    } catch (err) {
      console.error('Failed to fetch correlated logs:', err);
      setCorrelated(prev => ({
        ...prev,
        [log.id]: { loading: false, error: err.response?.data?.detail || 'Failed to load logs', lines: [] },
      }));
    }
  };

  // Shared by the global list and each expanded café's nested list.
  const applyFilters = (list) => {
    let result = list;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        String(l.user).toLowerCase().includes(q) ||
        String(l.details).toLowerCase().includes(q)
      );
    }
    if (severityFilter !== 'all') result = result.filter(l => l.severity === severityFilter);
    if (roleFilter !== 'all') result = result.filter(l => l.roleBucket === roleFilter);
    return result;
  };

  const filtered = useMemo(() => applyFilters(logs), [logs, searchQuery, severityFilter, roleFilter]);

  // Exports the unfiltered cross-cafe log set (not scoped to any expanded
  // café row — those are viewed inline instead of downloaded).
  const exportLogs = () => {
    if (!logs.length) return;
    const headers = ['ID', 'Timestamp', 'User', 'Role', 'Cafe', 'Action', 'Details', 'Severity', 'IP'];
    const escapeCsv = (val) => {
      const s = String(val ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = logs.map((l) => (
      [l.id, l.timestamp, l.user, l.role, l.cafeName || '', l.action, l.details, l.severity, l.ip].map(escapeCsv).join(',')
    ));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // One log entry — shared by the global list and every expanded café's
  // nested list below, so both render identically.
  const renderLogItem = (log) => {
    const ActionIcon = ACTION_CONFIG[log.action]?.icon || Activity;
    const isFailure = log.severity === 'warning' || log.severity === 'danger';
    const isExpanded = expandedLogId === log.id;
    const corr = correlated[log.id];
    return (
      <div key={log.id} className={`log-item glass-card log-item--${log.severity}`}>
        <div className="log-item__icon"><ActionIcon size={18} /></div>
        <div className="log-item__content">
          <div className="log-item__header">
            <span className="log-item__action">{ACTION_CONFIG[log.action]?.label || log.action}</span>
            {log.role && (
              <span className={`badge badge--${log.roleBucket === 'superadmin' ? 'primary' : 'neutral'}`}>
                {log.roleBucket === 'superadmin' ? 'Super Admin' : log.roleBucket === 'admin' ? 'Portal Admin' : log.role}
              </span>
            )}
          </div>
          <p className="log-item__details">{log.details}</p>
          <div className="log-item__meta">
            <span className="log-item__user"><User size={12} /> {log.user}</span>
            {showCafePanel && log.cafeName && (
              <span className="log-item__cafe"><Building2 size={12} /> {log.cafeName}</span>
            )}
            <span className="log-item__time"><Calendar size={12} /> {log.timestamp}</span>
          </div>
          {isFailure && (
            <button className="log-item__logs-toggle" onClick={() => toggleCorrelatedLogs(log)}>
              <Terminal size={12} />
              {isExpanded ? 'Hide backend logs' : 'Show backend logs around this time'}
            </button>
          )}
          {isExpanded && (
            <div className="sys-logs sys-logs--inline">
              {corr?.loading ? (
                <p className="sys-empty"><Loader2 size={14} className="spin" /> Loading...</p>
              ) : corr?.error ? (
                <p className="sys-error">{corr.error}</p>
              ) : !corr?.lines?.length ? (
                <p className="sys-empty">No raw log lines found in this window — Loki may not be reachable, or nothing was emitted at that moment.</p>
              ) : (
                corr.lines.map((entry, i) => (
                  <div key={i} className="sys-line">
                    <span className="sys-line__ts">{formatTs(entry.ts)}</span>
                    <span className="sys-line__text">{entry.line}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <span className={`badge badge--${SEVERITY_CONFIG[log.severity]}`}>{log.severity}</span>
      </div>
    );
  };

  // Loading/error/empty/list wrapper — shared by the global section and each
  // expanded café's nested section.
  const renderLogsBody = (list, { loading, error: bodyError, emptyLabel }) => {
    if (loading && !list.length) return <p className="sys-empty"><Loader2 size={16} className="spin" /> Loading...</p>;
    if (bodyError) return <p className="sys-error">{bodyError}</p>;
    const shown = applyFilters(list);
    if (shown.length === 0) return <p className="sys-empty">{list.length === 0 ? emptyLabel : 'No logs match the current filters.'}</p>;
    return <div className="logs-list">{shown.map(renderLogItem)}</div>;
  };

  return (
    <div className="audit-logs">
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">Activity history and system events</p>
        </div>
        <div className="header-actions">
          <div className="log-range-controls">
            <select value={logRange} onChange={(e) => setLogRange(e.target.value)} className="input select log-range-select">
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Custom range</option>
            </select>
            {logRange === 'custom' && (
              <div className="log-range-picker">
                <input
                  type="date"
                  value={customLogStart}
                  max={customLogEnd || todayIso}
                  onChange={(e) => setCustomLogStart(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomLogRange()}
                  aria-label="Start date"
                  className="input log-range-picker__input"
                />
                <span className="log-range-picker__sep">–</span>
                <input
                  type="date"
                  value={customLogEnd}
                  min={customLogStart || undefined}
                  max={todayIso}
                  onChange={(e) => setCustomLogEnd(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomLogRange()}
                  aria-label="End date"
                  className="input log-range-picker__input"
                />
                <button
                  type="button"
                  className="btn btn--secondary log-range-picker__apply"
                  onClick={applyCustomLogRange}
                  disabled={!customLogStart || !customLogEnd || isLoading}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
          {canExport && (
            <button className="btn btn--secondary" onClick={exportLogs} disabled={isLoading || !logs.length}>
              <Download size={16} /> Export
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={16} className="search-bar__icon" />
          <input type="text" placeholder="Search logs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-bar__input" />
        </div>
        <div className="filter-tabs">
          {['all', 'info', 'warning', 'danger'].map(sev => (
            <button key={sev} className={`filter-tab ${severityFilter === sev ? 'filter-tab--active' : ''}`} onClick={() => setSeverityFilter(sev)}>
              {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
            </button>
          ))}
        </div>
        <div className="filter-tabs">
          {['all', 'superadmin', 'admin'].map(rb => (
            <button
              key={rb}
              className={`filter-tab ${roleFilter === rb ? 'filter-tab--active' : ''}`}
              onClick={() => setRoleFilter(rb)}
            >
              {ROLE_BUCKET_LABEL[rb]}
            </button>
          ))}
        </div>
      </div>

      {/* Per-café accordion — expand a café's arrow to show just its logs.
          Multiple cafés can be expanded at once, each with its own section. */}
      {showCafePanel && (
        <div className="cafe-accordion glass-card">
          <div className="cafe-accordion__search">
            <Building2 size={14} />
            <input
              type="text"
              placeholder="Search cafés..."
              value={cafeSearch}
              onChange={(e) => setCafeSearch(e.target.value)}
            />
          </div>
          {cafesLoading ? (
            <div className="cafe-accordion__status"><Loader2 size={14} className="spin" /> Loading cafés…</div>
          ) : cafesError ? (
            <div className="cafe-accordion__status cafe-accordion__status--error">
              {cafesError}
              <button type="button" className="cafe-accordion__retry" onClick={loadCafes}>Retry</button>
            </div>
          ) : visibleCafes.length === 0 ? (
            <div className="cafe-accordion__status">No cafés match</div>
          ) : (
            visibleCafes.map(c => {
              const isOpen = !!expandedCafes[c.id];
              const cafeLogs = cafeLogsById[c.id];
              return (
                <div key={c.id} className="cafe-accordion__item">
                  <button type="button" className="cafe-accordion__row" onClick={() => toggleCafeExpand(c.id)}>
                    <ChevronDown size={14} className={`cafe-accordion__chevron ${isOpen ? 'cafe-accordion__chevron--open' : ''}`} />
                    <span>{c.name}</span>
                  </button>
                  {isOpen && (
                    <div className="cafe-accordion__logs">
                      {renderLogsBody(cafeLogs?.logs || [], {
                        loading: cafeLogs?.loading,
                        error: cafeLogs?.error,
                        emptyLabel: 'No audit log entries yet for this café.',
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {error && <p className="sys-error">{error}</p>}

      {/* Default cross-café log list — hidden while one or more cafés above
          are expanded, so the page isn't showing two overlapping views. */}
      {!anyCafeExpanded && renderLogsBody(logs, { loading: isLoading, error: null, emptyLabel: 'No audit log entries yet.' })}

      <style>{`
        .audit-logs {
          /* noLag rebrand: scoped to this page only. Confirmed via
             get_design_context on 2561:721 ("Audit Logs") — title/subtitle
             match exactly. The 4 stat cards Figma shows here are the same
             Total Users/Super Admins/Active/Trial Conversion mock reused
             from the Users & Roles template (not real Audit Logs content),
             so not adopted. The active filter tab IS shown orange-bordered
             in Figma ("All 12" vs plain-gray "Info"/"Warning"), matching
             .filter-tab--active below exactly — no change needed there.
             SEVERITY_CONFIG (info/warning/danger) is a separate --status-*
             token, untouched; every visible log-item in Figma's mock
             happens to be "Info" severity, so it shows no colored
             border-left example — that's Figma's sample data, not a design
             instruction to remove the real warning/danger border below. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1000px;
        }
        /* Figma-confirmed white-sheen recipe (2561:1071 etc.) for both
           .cafe-accordion and .log-item (both use .glass-card).
           Deliberately no border-left here: .log-item--warning/danger
           further down set border-left-color to the real severity — this
           rule's specificity (0,2,0) would otherwise beat theirs (0,1,0)
           regardless of source order and silently flatten every log to
           the same border. Each consumer sets its own border-left below,
           at the same 0,1,0 specificity the severity modifiers use, so
           normal source-order cascade (severity rules declared later)
           still wins exactly like it did before this rule existed. */
        .audit-logs .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          border-top: 1px solid rgba(255, 255, 255, 0.18);
          border-right: 1px solid rgba(255, 255, 255, 0.18);
          border-bottom: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
        }
        .audit-logs .glass-card::before { display: none; }
        .cafe-accordion, .log-item { border-left: 1px solid rgba(255, 255, 255, 0.18); }

        /* .cafe-accordion sits directly on the page (not nested) — safe to
           blur its own backdrop. */
        .audit-logs .cafe-accordion {
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
        }

        /* .log-item can be nested INSIDE .cafe-accordion (a café's own
           expanded log list) or sit directly on the page (the default
           cross-café view) — no backdrop-filter of its own, same fix as
           SystemHealth's nested metric tiles: a second saturate(180%) here
           would sample the accordion's already-boosted backdrop and
           compound toward the blown-out purple look. The dark base color
           (gradient bottoms out at 4% white, effectively transparent
           without it) also keeps it from just showing whatever page-
           background blob happens to sit behind it when NOT nested. */
        .audit-logs .log-item {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
        }
        .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-6); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .header-actions { display: flex; gap: var(--space-3); align-items: center; flex-wrap: wrap; }
        .log-range-controls { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
        .log-range-select { font-size: var(--text-xs); width: 150px; }
        .log-range-picker { display: inline-flex; align-items: center; gap: 6px; }
        .log-range-picker__input { font-size: var(--text-xs); width: 132px; padding: 6px 8px; }
        .log-range-picker__sep { font-size: var(--text-xs); color: var(--text-tertiary); }
        .log-range-picker__apply { padding: 6px 12px; font-size: var(--text-xs); }

        .filters-row { display: flex; gap: var(--space-4); margin-bottom: var(--space-6); align-items: center; flex-wrap: wrap; }
        .search-bar { position: relative; flex: 1; max-width: 320px; }
        .search-bar__icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--text-tertiary); }
        .search-bar__input {
          width: 100%; padding: 12px 14px 12px 40px; font-size: var(--text-sm);
          background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: var(--radius-md); color: var(--text-primary);
        }
        .search-bar__input::placeholder { color: var(--text-quaternary); }
        .search-bar__input:focus { outline: none; border-color: var(--accent-primary); box-shadow: var(--shadow-focus); }

        .filter-tabs { display: flex; gap: var(--space-2); }
        .filter-tab {
          padding: var(--space-2) var(--space-4); background: transparent;
          border: 1px solid var(--glass-border); border-radius: var(--radius-md);
          font-size: var(--text-sm); color: var(--text-secondary); cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }
        .filter-tab:hover { border-color: var(--glass-border-hover); background: var(--glass-bg); }
        .filter-tab--active { background: var(--accent-primary-subtle); border-color: var(--accent-primary); color: var(--accent-primary); }

        .cafe-accordion { margin-bottom: var(--space-6); padding: var(--space-3); }
        .cafe-accordion__search {
          display: flex; align-items: center; gap: var(--space-2);
          padding: var(--space-2) var(--space-3); margin-bottom: var(--space-2);
          background: var(--glass-bg); border: 1px solid var(--glass-border);
          border-radius: var(--radius-sm); color: var(--text-tertiary);
        }
        .cafe-accordion__search input {
          flex: 1; background: transparent; border: none; outline: none;
          color: var(--text-primary); font-size: var(--text-sm);
        }
        .cafe-accordion__item + .cafe-accordion__item { border-top: 1px solid var(--glass-border); }
        .cafe-accordion__row {
          width: 100%; display: flex; align-items: center; gap: var(--space-2);
          padding: var(--space-3); background: transparent; border: none;
          color: var(--text-primary); font-size: var(--text-sm); font-weight: 500; cursor: pointer;
          text-align: left; transition: background var(--duration-fast) var(--ease-out);
        }
        .cafe-accordion__row:hover { background: var(--glass-bg); }
        .cafe-accordion__chevron { color: var(--text-tertiary); flex-shrink: 0; transition: transform var(--duration-fast) var(--ease-out); }
        .cafe-accordion__chevron--open { transform: rotate(180deg); }
        .cafe-accordion__logs { padding: 0 var(--space-3) var(--space-4) var(--space-8); }
        .cafe-accordion__status {
          display: flex; align-items: center; justify-content: center; gap: var(--space-2);
          padding: var(--space-4); font-size: var(--text-xs); color: var(--text-tertiary); text-align: center;
        }
        .cafe-accordion__status--error { color: var(--status-danger); flex-direction: column; }
        .cafe-accordion__retry {
          background: transparent; border: 1px solid var(--glass-border); border-radius: var(--radius-sm);
          color: var(--text-secondary); font-size: var(--text-xs); padding: var(--space-1) var(--space-3); cursor: pointer;
        }
        .cafe-accordion__retry:hover { border-color: var(--glass-border-hover); color: var(--text-primary); }

        .logs-list { display: flex; flex-direction: column; gap: var(--space-4); }
        .log-item { display: flex; gap: var(--space-4); padding: var(--space-5); align-items: flex-start; }
        .log-item--warning { border-left: 3px solid var(--status-warning); }
        .log-item--danger { border-left: 3px solid var(--status-danger); }

        .log-item__icon {
          width: 36px; height: 36px; background: rgba(255,255,255,0.06);
          border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center;
          color: var(--text-tertiary); flex-shrink: 0;
        }
        .log-item__content { flex: 1; min-width: 0; }
        .log-item__header { display: flex; align-items: center; gap: var(--space-2); margin-bottom: var(--space-2); }
        .log-item__action { font-weight: 600; font-size: var(--text-sm); }
        .log-item__details { font-size: var(--text-sm); color: var(--text-tertiary); margin-bottom: var(--space-3); }
        .log-item__meta { display: flex; gap: var(--space-4); font-size: var(--text-xs); color: var(--text-quaternary); }
        .log-item__user, .log-item__time, .log-item__cafe { display: flex; align-items: center; gap: var(--space-1); }
        .log-item__logs-toggle {
          display: flex; align-items: center; gap: var(--space-1); margin-top: var(--space-3);
          padding: var(--space-1) var(--space-2); background: transparent; border: 1px dashed var(--glass-border);
          border-radius: var(--radius-sm); font-size: var(--text-xs); color: var(--accent-primary); cursor: pointer;
        }
        .log-item__logs-toggle:hover { background: var(--glass-bg); border-color: var(--accent-primary); }
        .sys-logs--inline { margin-top: var(--space-3); max-height: 280px; }

        .sys-error { font-size: var(--text-sm); color: var(--status-danger); margin-bottom: var(--space-4); }
        .sys-empty {
          display: flex; align-items: center; gap: var(--space-2); justify-content: center;
          padding: var(--space-6); font-size: var(--text-sm); color: var(--text-tertiary);
        }
        .sys-logs {
          display: flex; flex-direction: column; font-family: var(--font-mono, ui-monospace, monospace);
          font-size: var(--text-xs); background: var(--glass-bg); border: 1px solid var(--glass-border);
          border-radius: var(--radius-md); max-height: 640px; overflow-y: auto;
        }
        .sys-line {
          display: flex; gap: var(--space-3); padding: var(--space-2) var(--space-4);
          border-bottom: 1px solid var(--glass-border);
        }
        .sys-line:last-child { border-bottom: none; }
        .sys-line__ts { flex-shrink: 0; color: var(--text-quaternary); white-space: nowrap; }
        .sys-line__text { color: var(--text-secondary); white-space: pre-wrap; word-break: break-word; }
        .spin { animation: audit-spin 1s linear infinite; }
        @keyframes audit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
